import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { DisplayNameEdit } from './DisplayNameEdit';
import { LocaleSwitcher } from './LocaleSwitcher';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

const HIDE_HEADER = new Set(['/play']);

function hideHeader(pathname: string) {
  if (HIDE_HEADER.has(pathname)) return true;
  if (/^\/archives\/[^/]+\/play$/.test(pathname)) return true;
  return false;
}

function navClass(pathname: string, match: string | RegExp) {
  const active = typeof match === 'string'
    ? pathname === match || (match !== '/' && pathname.startsWith(match))
    : match.test(pathname);
  return active ? 'active' : undefined;
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`site-header-menu-icon${open ? ' is-open' : ''}`}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path className="site-header-menu-bar" d="M4 7h16" />
      <path className="site-header-menu-bar" d="M4 12h16" />
      <path className="site-header-menu-bar" d="M4 17h16" />
    </svg>
  );
}

type DropdownId = 'leaderboards' | 'history';

function NavDropdown({
  id,
  openId,
  onOpen,
  onScheduleClose,
  toggleTo,
  toggleLabel,
  toggleActive,
  links,
}: {
  id: DropdownId;
  openId: DropdownId | null;
  onOpen: (id: DropdownId) => void;
  onScheduleClose: () => void;
  toggleTo: string;
  toggleLabel: string;
  toggleActive: boolean;
  links: { to: string; label: string }[];
}) {
  return (
    <div
      className={`site-header-dropdown${openId === id ? ' is-open' : ''}`}
      onMouseEnter={() => onOpen(id)}
      onMouseLeave={onScheduleClose}
    >
      <Link
        to={toggleTo}
        className={`site-header-dropdown-toggle${toggleActive ? ' active' : ''}`}
        aria-haspopup="true"
        aria-expanded={openId === id}
      >
        {toggleLabel}
      </Link>
      <div className="site-header-dropdown-menu">
        {links.map((link) => (
          <Link key={link.to} to={link.to}>{link.label}</Link>
        ))}
      </div>
    </div>
  );
}

export function SiteHeader() {
  const { pathname } = useLocation();
  const { user, guest, loading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownId | null>(null);
  const closeDropdownTimer = useRef<number | null>(null);
  const hidden = hideHeader(pathname);

  const lbActive = pathname.startsWith('/leaderboard');
  const closeMenu = () => setMenuOpen(false);

  const openDropdownNow = (id: DropdownId) => {
    if (closeDropdownTimer.current) window.clearTimeout(closeDropdownTimer.current);
    setOpenDropdown(id);
  };

  const scheduleCloseDropdown = () => {
    if (closeDropdownTimer.current) window.clearTimeout(closeDropdownTimer.current);
    closeDropdownTimer.current = window.setTimeout(() => setOpenDropdown(null), 220);
  };

  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [pathname]);

  useEffect(() => {
    if (hidden || !menuOpen) {
      document.body.classList.remove('nav-open');
      return;
    }
    document.body.classList.add('nav-open');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('nav-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, hidden]);

  if (hidden) return null;

  const leaderboardLinks = [
    { to: '/leaderboard', label: t('leaderboard.periodToday') },
    { to: '/leaderboard/semaine', label: t('leaderboard.periodWeek') },
    { to: '/leaderboard/mois', label: t('leaderboard.periodMonth') },
    { to: '/leaderboard/tous-les-temps', label: t('leaderboard.periodAllTime') },
  ];

  const historyActive = pathname.startsWith('/archives') || pathname.startsWith('/top');

  const historyLinks = [
    { to: '/archives', label: t('archives.title') },
    { to: '/top', label: t('nav.top') },
  ];

  const mainLinks = [
    { to: '/decouvrir', label: t('nav.discover'), match: '/decouvrir' as const },
    { to: '/comment-jouer', label: t('nav.howToPlay'), match: '/comment-jouer' as const },
  ];

  const accountBlock = (onNavigate?: () => void) => (
    <>
      <LocaleSwitcher />
      {loading ? (
        <span className="site-header-muted">…</span>
      ) : user ? (
        <DisplayNameEdit />
      ) : (
        <Link to="/login" className="site-header-login" onClick={onNavigate}>
          {t('landing.login')}
        </Link>
      )}
    </>
  );

  const mobileMenu = menuOpen
    ? createPortal(
      <div className="site-header-mobile-overlay is-open" role="dialog" aria-modal="true" aria-label={t('nav.main')}>
        <button
          type="button"
          className="site-header-backdrop"
          onClick={closeMenu}
        >
          <span className="sr-only">{t('nav.closeMenu')}</span>
        </button>

        <nav id="site-header-nav-mobile" className="site-header-nav site-header-nav--mobile">
          <Link to="/play" className={navClass(pathname, '/play')} onClick={closeMenu}>
            {t('nav.daily')}
          </Link>

          <div className="site-header-nav-group">
            <span className="site-header-nav-label">{t('nav.leaderboards')}</span>
            {leaderboardLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={navClass(pathname, link.to === '/leaderboard' ? '/leaderboard' : link.to)}
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="site-header-nav-group">
            <span className="site-header-nav-label">{t('nav.history')}</span>
            {historyLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={navClass(pathname, link.to)}
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {mainLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={navClass(pathname, link.match)}
              onClick={closeMenu}
            >
              {link.label}
            </Link>
          ))}

          {user && !guest && (
            <Link to="/friends" className={navClass(pathname, '/friends')} onClick={closeMenu}>
              {t('nav.friends')}
            </Link>
          )}

          {!user?.isPremium && (
            <Link to="/premium" className={navClass(pathname, '/premium')} onClick={closeMenu}>
              {t('premium.title')}
            </Link>
          )}
          {user?.isAdmin && (
            <Link to="/admin" className={navClass(pathname, '/admin')} onClick={closeMenu}>
              {t('nav.admin')}
            </Link>
          )}

          <div className="site-header-mobile-foot">
            {accountBlock(closeMenu)}
          </div>
        </nav>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <header className={`site-header${menuOpen ? ' is-menu-open' : ''}`}>
        <Link to="/" className="site-header-logo" onClick={closeMenu}>
          Dezoom
        </Link>

        <nav className="site-header-nav site-header-nav--desktop" aria-label={t('nav.main')}>
          <Link to="/play" className={navClass(pathname, '/play')}>{t('nav.daily')}</Link>

          <NavDropdown
            id="leaderboards"
            openId={openDropdown}
            onOpen={openDropdownNow}
            onScheduleClose={scheduleCloseDropdown}
            toggleTo="/leaderboard"
            toggleLabel={t('nav.leaderboards')}
            toggleActive={lbActive}
            links={leaderboardLinks}
          />

          <NavDropdown
            id="history"
            openId={openDropdown}
            onOpen={openDropdownNow}
            onScheduleClose={scheduleCloseDropdown}
            toggleTo="/archives"
            toggleLabel={t('nav.history')}
            toggleActive={historyActive}
            links={historyLinks}
          />

          {mainLinks.map((link) => (
            <Link key={link.to} to={link.to} className={navClass(pathname, link.match)}>
              {link.label}
            </Link>
          ))}

          {user && !guest && (
            <Link to="/friends" className={navClass(pathname, '/friends')}>
              {t('nav.friends')}
            </Link>
          )}

          {!user?.isPremium && (
            <Link to="/premium" className={navClass(pathname, '/premium')}>
              {t('premium.title')}
            </Link>
          )}
          {user?.isAdmin && (
            <Link to="/admin" className={navClass(pathname, '/admin')}>
              {t('nav.admin')}
            </Link>
          )}
        </nav>

        <div className="site-header-account site-header-account--desktop">
          {accountBlock()}
        </div>

        <button
          type="button"
          className="site-header-menu-btn"
          aria-expanded={menuOpen}
          aria-controls="site-header-nav-mobile"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="sr-only">
            {menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          </span>
          <MenuIcon open={menuOpen} />
        </button>
      </header>

      {mobileMenu}
    </>
  );
}
